import React, { ReactElement, useState } from "react";
import { useQuery } from "@apollo/client";
import { useFlag } from "@unleash/proxy-client-react";
import { useTranslation } from "react-i18next";

import { GetWine_wine } from "../../../types/__generated__/GetWine";
import { getAuth } from "../../../types/__generated__/getAuth";
import {
  GetWineRelatedBadges,
  GetWineRelatedBadges_wine_relatedBadges as WineRelatedBadgeObject,
} from "../../../types/__generated__/GetWineRelatedBadges";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { MemberRewardBasicInfo } from "../../../types/__generated__/MemberRewardBasicInfo";
import { GET_AUTH } from "../../../graphql/resolvers/auth";
import { WINE_RELATED_BADGES } from "../../../graphql/resolvers/wine";
import { ChildChallengeBadges_memberChildBadgesFromChallengeBadge } from "../../../types/__generated__/ChildChallengeBadges";
import { BadgeRelatedWines_badgeRelatedWines_badgeAlreadyDrunkWines as ChallengePastWine } from "../../../types/__generated__/BadgeRelatedWines";

import {
  BADGE_CATEGORY_IDS,
  FETCH_POLICY_CACHE_ONLY,
  I18N_NAMESPACES,
  UNLEASH_TOPUP_V2,
} from "../../../helpers/constants";
import badgeDefault from "../../../assets/images/icons/badge-default.svg";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import BadgeItem from "../BadgeItem";
import BadgeInfoTray from "../BadgeInfoTray";

import { useDeviceSize } from "../../../helpers/hooks";
import { getWineBaseColor } from "../../../helpers/tools";

import ChallengeInfoTray from "../ChallengeInfoTray";
import ChallengeOptions from "../../atoms/ChallengeOptions";
import ChallengeGameplay from "../../atoms/ChallengeGameplay";
import WineDetailsTray from "../../organisms/WineDetailsTray";
import cellarBottleImg from "../../../assets/images/temp/bottle-xl-vertical.png";
import TopUpTray from "../../organisms/TopUpTray";
import BuyAgainTray from "../../organisms/BuyAgainTray";

import { ContentContainer, Header, BadgeContainer, BadgeWrapper } from "./style";

interface WineRelatedBadgesProps {
  wine: GetWine_wine | null | undefined;
  memberShoppingCartQuery: MemberShoppingCart | MemberRewardBasicInfo | null | undefined;
  isInBoxAndCartTray?: boolean;
  isWineDetailsPage?: boolean;
}

const WineRelatedBadges = ({
  wine,
  memberShoppingCartQuery,
  isInBoxAndCartTray = false,
  isWineDetailsPage,
}: WineRelatedBadgesProps) => {
  const isTopupV2Enabled = useFlag(UNLEASH_TOPUP_V2);
  const { t } = useTranslation(I18N_NAMESPACES.WINE_PAGES);

  const [selectedBadge, setSelectedBadge] = useState<WineRelatedBadgeObject | null>(null);

  // States for challenge info tray
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedChildBadge, setSelectedChildBadge] = useState<any>(null);
  const [shouldShowChallengeOptions, setShouldShowChallengeOptions] = useState<boolean>(false);
  const [shouldShowChallengeGameplay, setShouldShowChallengeGameplay] = useState<boolean>(false);
  const [selectedWineSlug, setSelectedWineSlug] = useState<string | null>(null);
  const [selectedTopUpWine, setSelectedTopUpWine] = useState<ChallengePastWine | null>(null);

  const { isSmall } = useDeviceSize();

  const { data: authData, loading: authLoading } = useQuery<getAuth>(GET_AUTH, {
    fetchPolicy: FETCH_POLICY_CACHE_ONLY,
  });

  const memberId = authData?.auth?.memberId;

  const {
    data: wineRelatedBadgesData,
    loading: wineRelatedBadgesLoading,
    refetch: refetchWineRelatedBadgesQuery,
  } = useQuery<GetWineRelatedBadges>(WINE_RELATED_BADGES, {
    skip: !memberId || !wine?.product?.slug,
    variables: {
      slug: wine?.product?.slug,
      earnedBadgeFilter: false,
    },
  });

  const isLoading = authLoading || wineRelatedBadgesLoading;

  const wineRelatedBadges = wineRelatedBadgesData?.wine?.relatedBadges ?? [];

  const renderWineRelatedBadgeContent = (): ReactElement => (
    <BadgeContainer>
      {wineRelatedBadges?.map((wineRelatedBadge) => (
        <BadgeWrapper key={wineRelatedBadge?.id} isMobile={isSmall}>
          <BadgeItem
            key={wineRelatedBadge?.id}
            onClick={() => setSelectedBadge(wineRelatedBadge)}
            title={wineRelatedBadge?.name}
            point={wineRelatedBadge?.points ?? 0}
            imageUrl={wineRelatedBadge?.imageSmallUrl ?? badgeDefault}
            isEarned={wineRelatedBadge?.isEarned}
            showPointsOrWineLevel
          />
        </BadgeWrapper>
      ))}
    </BadgeContainer>
  );

  const renderWineRelatedBadgeInfoTray = (): ReactElement => (
    <BadgeInfoTray
      badge={selectedBadge}
      onClose={() => setSelectedBadge(null)}
      refetchBadges={() => refetchWineRelatedBadgesQuery()}
      memberShoppingCartQuery={memberShoppingCartQuery}
      isInBoxAndCartTray={isInBoxAndCartTray}
    />
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderChallengeChildBadgeInfoTray = (badge: any | null): ReactElement => (
    <BadgeInfoTray
      badge={badge}
      onClose={() => setSelectedChildBadge(null)}
      refetchBadges={() => refetchWineRelatedBadgesQuery()}
      memberShoppingCartQuery={memberShoppingCartQuery}
      isInBoxAndCartTray={isInBoxAndCartTray}
    />
  );

  const renderChallengeOptionsTray = (): ReactElement => (
    <>
      <ChallengeOptions
        /* @ts-ignore */
        challengeBadge={selectedBadge}
        onOpenChallengeGameplay={() => setShouldShowChallengeGameplay(true)}
        onClose={() => setShouldShowChallengeOptions(false)}
        onUpdateChallengeStatus={() => refetchWineRelatedBadgesQuery()}
        isInBoxAndCartTray={isInBoxAndCartTray}
      />
    </>
  );

  const renderChallengeGameplayTray = (): ReactElement => (
    <>
      <ChallengeGameplay
        /* @ts-ignore */
        challenge={selectedBadge}
        onClose={() => setShouldShowChallengeGameplay(false)}
        isInBoxAndCartTray={isInBoxAndCartTray}
      />
    </>
  );

  const renderWineDetailsTray = (): ReactElement => (
    <WineDetailsTray
      handleToggleBottleInfoTray={() => setSelectedWineSlug(null)}
      memberShoppingCartQuery={memberShoppingCartQuery}
      slug={selectedWineSlug}
      isInBoxAndCartTray={isInBoxAndCartTray}
    />
  );

  const renderTopUpTray = (): ReactElement => {
    const baseColor = getWineBaseColor(selectedTopUpWine?.wineType?.wineClass?.id ?? 0);
    const bottleImage = selectedTopUpWine?.product?.productPhotos?.[0]?.photoSmall || cellarBottleImg;

    return (
      <TopUpTray
        isOffCanvasTopUpOpened
        handleOffCanvasTopUpChange={() => setSelectedTopUpWine(null)}
        wine={selectedTopUpWine}
        baseColor={baseColor}
        bottleImage={bottleImage}
        isInBoxAndCartTray={isInBoxAndCartTray}
      />
    );
  };

  const renderBuyAgainTray = (): ReactElement => {
    const baseColor = getWineBaseColor(selectedTopUpWine?.wineType?.wineClass?.id ?? 0);
    const bottleImage = selectedTopUpWine?.product?.productPhotos?.[0]?.photoSmall || cellarBottleImg;
    const wineSlug = selectedTopUpWine?.product?.slug!;

    return (
      <BuyAgainTray
        handleOffCanvasBuyAgainChange={() => setSelectedTopUpWine(null)}
        wineSlug={wineSlug}
        baseColor={baseColor}
        bottleImage={bottleImage}
        isInBoxAndCartTray={isInBoxAndCartTray}
      />
    );
  };

  const renderWineRelateBadgeInfoTray = (): ReactElement => {
    const badgeCategoryId = selectedBadge?.badgeCategory?.id;
    const isChallengeBadge = badgeCategoryId === BADGE_CATEGORY_IDS.DB_ID_BADGE_CATEGORY_CHALLENGES;

    if (isChallengeBadge) {
      return (
        <>
          <ChallengeInfoTray
            /* @ts-ignore */
            challengeBadge={selectedBadge}
            memberShoppingCartQuery={memberShoppingCartQuery}
            onClose={() => setSelectedBadge(null)}
            memberId={memberId}
            onChildBadgeClick={(childBadge: ChildChallengeBadges_memberChildBadgesFromChallengeBadge) =>
              setSelectedChildBadge(childBadge)
            }
            onWineDetailsClick={(wineSlug: string) => setSelectedWineSlug(wineSlug)}
            onTopUpClick={(topUpWine: ChallengePastWine) => setSelectedTopUpWine(topUpWine)}
            onUpdateChallengeStatus={() => refetchWineRelatedBadgesQuery()}
            isChallengeBadgesQueryLoading={wineRelatedBadgesLoading}
            onOptionsClick={() => setShouldShowChallengeOptions(true)}
            isInBoxAndCartTray={isInBoxAndCartTray}
          />
        </>
      );
    }

    // Non-challenge badge category
    return renderWineRelatedBadgeInfoTray();
  };

  // For null cases, doesn't render this section.
  if (!wineRelatedBadgesLoading && wineRelatedBadges?.length === 0) {
    return null;
  }

  return (
    <>
      <ContentContainer isWineDetailsPage={!!isWineDetailsPage}>
        <Header>{t("winePages:wineRelatedBadgesTitle")}</Header>
        {isLoading ? <StyledLoading /> : renderWineRelatedBadgeContent()}
      </ContentContainer>

      {selectedBadge && renderWineRelateBadgeInfoTray()}

      {shouldShowChallengeOptions && renderChallengeOptionsTray()}

      {shouldShowChallengeGameplay && renderChallengeGameplayTray()}

      {selectedChildBadge && renderChallengeChildBadgeInfoTray(selectedChildBadge)}

      {selectedWineSlug && renderWineDetailsTray()}

      {!isTopupV2Enabled && selectedTopUpWine && renderTopUpTray()}
      {isTopupV2Enabled && selectedTopUpWine && renderBuyAgainTray()}
    </>
  );
};

export default WineRelatedBadges;
